<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import BaseSkeleton from "@/components/base/BaseSkeleton.vue";

const { t } = useI18n();
</script>

<template>
  <!--
    Скелет главной вместо крутящегося лоадера: повторяет сетку и размеры блоков
    из Account.vue, поэтому при загрузке страница не «прыгает», а заполняется.
    Классы раскладки переиспользуются из account.scss — сетка остаётся общей
    с реальным экраном и не расходится с ним при правках.
  -->
  <div
    class="account account-skeleton"
    role="status"
    aria-busy="true"
    :aria-label="t('common.labels.loadingAlt')"
  >
    <div class="grid-block account-balance">
      <div class="account-balance__title">
        <BaseSkeleton width="88px" height="14px" />
      </div>
      <div class="account-balance__value">
        <BaseSkeleton width="132px" height="34px" radius="10px" />
      </div>
      <div class="account-balance__btn">
        <BaseSkeleton
          class="account-skeleton__balance-btn"
          height="44px"
          radius="16px"
        />
      </div>
    </div>

    <!-- Тариф — цветная карточка во всю ячейку, поэтому одна большая заглушка. -->
    <div class="grid-block account-tariff">
      <BaseSkeleton class="account-skeleton__tariff" radius="32px" />
    </div>

    <div class="grid-block account-history">
      <BaseSkeleton
        class="account-history--pic"
        width="48px"
        height="48px"
        radius="50%"
      />
      <BaseSkeleton width="176px" height="18px" />
      <div class="account-history--link">
        <BaseSkeleton width="24px" height="24px" radius="50%" />
      </div>
    </div>

    <div class="grid-block account-bottom account-skeleton__addons">
      <BaseSkeleton
        class="account-skeleton__addons-title"
        width="212px"
        height="22px"
      />
      <div class="account-skeleton__addons-list">
        <BaseSkeleton
          v-for="index in 3"
          :key="index"
          class="account-skeleton__addons-item"
          radius="18px"
        />
      </div>
    </div>

    <div class="grid-block account-services">
      <div class="account-services__title">
        <BaseSkeleton width="128px" height="22px" />
      </div>
      <div class="account-services__body">
        <!--
          Две плитки, а не четыре как в wl-pwa-new: столько пунктов отдаёт
          servicesMenu в этом проекте (премиум-номера и роуминг).
        -->
        <BaseSkeleton
          v-for="index in 2"
          :key="index"
          class="account-skeleton__service"
          radius="18px"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/components/account";

.account-skeleton {
  // Заглушки не кликаются и не выделяются — это просто холст.
  user-select: none;
  pointer-events: none;

  &__balance-btn {
    max-width: 148px;
  }

  &__tariff {
    // На десктопе высоту задаёт строка сетки, на мобильном — сама карточка
    // тарифа. В этом проекте её height закомментирован в TariffMain, высота
    // идёт от контента, поэтому повторяем исходные 245px из макета.
    height: 100%;

    @media (max-width: 768px) {
      height: 245px;
    }
  }

  &__addons {
    padding: 16px 24px 24px;

    @media (max-width: 768px) {
      padding: 0;
    }
  }

  &__addons-title {
    margin-bottom: 24px;

    // Заголовок «Подключённые опции» на мобильном скрыт и в реальном блоке.
    @media (max-width: 768px) {
      display: none;
    }
  }

  &__addons-list {
    display: flex;
    flex-flow: row;
    gap: 10px;

    // Плитки опций: три в ряд на десктопе, столбик на мобильном — как в
    // AddonsMain (swiper против обычного списка).
    @media (max-width: 768px) {
      flex-flow: column;
      gap: 12px;
    }
  }

  &__addons-item {
    flex: 1;
    height: 180px;

    @media (max-width: 768px) {
      height: 96px;
    }
  }

  &__service {
    max-width: 185px;
    width: 100%;
    height: 168px;

    @media (max-width: 768px) {
      max-width: unset;
      height: 100%;
    }
  }
}
</style>
